import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEG_ORDER = ['AGE', 'GENDER', 'PLACEMENT', 'DEVICE'];
const SEG_LABELS = {
  AGE: 'Age',
  GENDER: 'Gender',
  PLACEMENT: 'Placement',
  DEVICE: 'Device',
};

const TIER_STYLES = {
  HIGH: 'bg-emerald-500/20 text-emerald-300',
  MEDIUM: 'bg-amber-500/20 text-amber-300',
  LOW: 'bg-slate-600/30 text-slate-400',
};

const DeltaBadge = ({ delta, outperforming, underperforming }) => {
  if (outperforming) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
        +{Math.round(delta * 100)}%
      </span>
    );
  }
  if (underperforming) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">
        {Math.round(delta * 100)}%
      </span>
    );
  }
  return <span className="text-xs text-slate-500">—</span>;
};

const SegmentCard = ({ segType, rows }) => (
  <div
    data-testid={`abh-audience-card-${segType.toLowerCase()}`}
    className="bg-slate-900/40 border border-slate-800 rounded-xl p-5"
  >
    <h3 className="text-slate-300 text-sm font-medium mb-3">{SEG_LABELS[segType]}</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <th className="py-1.5 px-2 font-normal">Value</th>
            <th className="py-1.5 px-2 font-normal text-right">Spend</th>
            <th className="py-1.5 px-2 font-normal text-right">Conv.</th>
            <th className="py-1.5 px-2 font-normal text-right">CPA</th>
            <th className="py-1.5 px-2 font-normal text-right">CTR</th>
            <th className="py-1.5 px-2 font-normal text-right">Δ avg</th>
            <th className="py-1.5 px-2 font-normal text-right">Conf.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.segment_value + i}
              data-testid={`segment-row-${segType.toLowerCase()}-${r.segment_value.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
              className={`border-b border-slate-800/50 ${r.outperforming ? 'bg-emerald-500/5' : r.underperforming ? 'bg-red-500/5' : ''}`}
            >
              <td className="py-1.5 px-2 text-slate-200">{r.segment_value}</td>
              <td className="py-1.5 px-2 text-slate-300 text-right">
                {r.spend ? `$${r.spend.toFixed(0)}` : '—'}
              </td>
              <td className="py-1.5 px-2 text-slate-300 text-right">{r.conversions || 0}</td>
              <td className="py-1.5 px-2 text-slate-300 text-right">
                {r.cpa ? `$${r.cpa.toFixed(2)}` : '—'}
              </td>
              <td className="py-1.5 px-2 text-slate-300 text-right">
                {r.ctr ? `${(r.ctr * 100).toFixed(2)}%` : '—'}
              </td>
              <td className="py-1.5 px-2 text-right">
                <DeltaBadge {...r} />
              </td>
              <td className="py-1.5 px-2 text-right">
                <span className={`text-[10px] px-1 py-0.5 rounded ${TIER_STYLES[r.confidence_tier] || TIER_STYLES.LOW}`}>
                  {r.confidence_tier}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ABHAudiencePage = () => {
  const [channel, setChannel] = useState('');
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const qs = channel ? `?channel=${channel}` : '';
      const [seg, ins] = await Promise.all([
        axios.get(`${API}/abh/audience/segments${qs}`),
        axios.get(`${API}/abh/audience/session-insights${qs}`),
      ]);
      setData(seg.data);
      setInsights(ins.data?.insights || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load audience intelligence');
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => { load(); }, [load]);

  const hasData = data?.has_data;

  return (
    <div data-testid="abh-audience-page" className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-white text-2xl font-semibold">ABH Audience Intelligence</h1>
          <p className="text-slate-400 text-sm mt-1">
            Which Meta segments are outperforming — completely siloed from AFFILIATE.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="abh-audience-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-700"
          >
            <option value="">All channels</option>
            <option value="META">Meta</option>
            <option value="TIKTOK">TikTok</option>
          </select>
          <button
            data-testid="abh-audience-refresh"
            onClick={load}
            className="text-sm px-3 py-1.5 rounded bg-slate-800 text-slate-200 hover:bg-slate-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {insights.length > 0 && (
        <div data-testid="abh-audience-session-insights" className="space-y-2">
          {insights.map((ins, i) => (
            <div
              key={i}
              className={`text-sm px-3 py-2 rounded-lg border ${
                ins.kind === 'OUTPERFORM'
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                  : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
              }`}
            >
              {ins.message}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : !hasData ? (
        <div
          data-testid="abh-audience-empty"
          className="bg-slate-900/60 border border-slate-800 rounded-xl p-8 text-center"
        >
          <p className="text-slate-300 font-medium">No audience data yet.</p>
          <p className="text-slate-500 text-sm mt-1.5 max-w-lg mx-auto">
            Run your first ABH campaign and connect Meta API to start building intelligence.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {SEG_ORDER.map((seg) => (
            <SegmentCard key={seg} segType={seg} rows={data.breakdown[seg] || []} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ABHAudiencePage;
